import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CalendarDays, DoorOpen, Mic2, MapPin, Accessibility, ShieldCheck } from "lucide-react";
import { fetchConcert, imgSrc } from "../lib/api";
import { CalendarShare } from "../components/CalendarShare";
import { Gallery } from "../components/Gallery";
import { cad, canBuy, fullDate, time12 } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { TicketButton } from "../components/TicketButton";
import { Reveal, Eyebrow } from "../components/Reveal";
import { FaqList } from "../sections/FAQ";

const Fact = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-[#12141C] p-5" data-testid={testId}>
    <Icon size={18} className="text-sunset" />
    <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-zinc-400">{label}</p>
    <p className="mt-1 font-display text-xl font-bold uppercase text-white">{value}</p>
  </div>
);

const TicketOptions = ({ c }) => (
  <div data-testid="ticket-options" className="rounded-[24px] border border-white/10 bg-[#12141C] p-6 sm:p-8">
    <div className="flex items-center justify-between gap-4">
      <h2 className="font-display text-3xl font-extrabold uppercase text-white">Tickets</h2>
      <StatusBadge status={c.is_past ? "Past" : c.status} testId="event-status" />
    </div>
    {c.ticket_options?.length ? (
      <ul className="mt-6 divide-y divide-white/10">
        {c.ticket_options.map((t, i) => (
          <li key={t.name} data-testid={`ticket-option-${i}`} className="flex items-center justify-between gap-4 py-4">
            <div><p className="font-semibold text-white">{t.name}</p><p className="text-sm text-zinc-400">{t.note}</p></div>
            <p className="font-display text-2xl font-extrabold text-white">{cad(t.price) ?? "TBA"}</p>
          </li>
        ))}
      </ul>
    ) : (
      <p className="mt-6 text-zinc-300">{c.is_past ? "This event has ended." : "Ticket prices will be announced soon. Sign up for alerts to hear first."}</p>
    )}
    <p className="mt-2 text-xs text-zinc-400">Prices in Canadian dollars (CAD). Fees and taxes may apply at checkout.</p>
    <TicketButton concert={c} testId="event-buy-tickets-btn" label="Buy Tickets" className="btn-lg mt-6 w-full justify-center" />
    {canBuy(c) && <p className="mt-3 text-center text-xs text-zinc-400">You'll complete your purchase on our official ticketing partner's site.</p>}
  </div>
);

export default function EventPage() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setC(null);
    setError(false);
    fetchConcert(slug).then((d) => (d ? setC(d) : setError(true))).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <main className="grid min-h-[80vh] place-items-center px-5 pt-24 text-center" data-testid="event-not-found">
      <div><h1 className="font-display text-5xl font-extrabold uppercase text-white">Show not found</h1><Link to="/#lineup" className="btn-primary mt-6">Back to lineup</Link></div>
    </main>
  );
  if (!c) return <main className="min-h-screen pt-32" data-testid="event-loading"><div className="mx-auto h-96 max-w-7xl animate-pulse rounded-3xl bg-white/5" /></main>;

  const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address || `${c.venue}, Niagara Falls, ON`)}`;

  return (
    <main data-testid="event-page">
      <section className="relative flex min-h-[78svh] items-end overflow-hidden">
        <motion.img initial={{ scale: 1.2 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }} src={imgSrc(c.image)} alt={`${c.artist} performing`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/50 to-[#0B0C10]/30" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-12 pt-32 lg:px-12">
          <Link to="/#lineup" data-testid="event-back-link" className="mb-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-200 hover:text-white"><ArrowLeft size={14} /> All shows</Link>
          {c.is_sample && <p data-testid="event-sample-notice" className="mb-4 inline-block rounded-full border border-white/20 bg-black/40 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-zinc-200">Sample event · details not final</p>}
          <Eyebrow>{c.genre}</Eyebrow>
          <h1 data-testid="event-artist-name" className="mt-3 overflow-hidden font-display text-6xl font-extrabold uppercase leading-[0.88] text-white sm:text-8xl lg:text-9xl">
            <motion.span className="block" initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}>{c.artist}</motion.span>
          </h1>
          {c.supporting_acts?.length > 0 && <p data-testid="event-supporting-acts" className="mt-4 text-lg text-zinc-200">with special guests {c.supporting_acts.join(" & ")}</p>}
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <TicketButton concert={c} testId="event-hero-buy-btn" label="Buy Tickets" className="btn-lg justify-center" />
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-12 lg:px-12">
        <div className="space-y-12 lg:col-span-7">
          <Reveal className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Fact icon={CalendarDays} label="Date" value={fullDate(c.date).replace(/, \d{4}$/, "")} testId="event-date" />
            <Fact icon={DoorOpen} label="Doors" value={time12(c.doors_time)} testId="event-doors" />
            <Fact icon={Mic2} label="Show" value={time12(c.show_time)} testId="event-show-time" />
          </Reveal>
          <Reveal>
            <Eyebrow>About the show</Eyebrow>
            <p data-testid="event-description" className="mt-4 text-lg leading-relaxed text-zinc-200">{c.description}</p>
          </Reveal>
          <Reveal data-testid="event-venue" className="rounded-[24px] border border-white/10 bg-[#12141C] p-6 sm:p-8">
            <MapPin className="text-sunset" />
            <h2 className="mt-4 font-display text-3xl font-extrabold uppercase text-white">{c.venue}</h2>
            <p className="mt-1 text-zinc-300">{c.address}</p>
            <p className="mt-5 text-sm leading-relaxed text-zinc-300">{c.directions}</p>
            <a href={maps} target="_blank" rel="noopener noreferrer" data-testid="event-directions-link" className="btn-ghost mt-6">Get Directions <ArrowUpRight size={15} /></a>
          </Reveal>
          <Reveal className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/10 p-6" data-testid="event-accessibility">
              <Accessibility className="text-[#C9A0F0]" />
              <p className="mt-3 font-display text-xl font-bold uppercase text-white">Accessibility</p>
              <p className="mt-1 text-sm text-zinc-300">Accessible viewing areas, companion seating, accessible washrooms and step-free entry. Service animals welcome.</p>
            </div>
            <div className="rounded-2xl border border-white/10 p-6" data-testid="event-policies">
              <ShieldCheck className="text-[#C9A0F0]" />
              <p className="mt-3 font-display text-xl font-bold uppercase text-white">Policies</p>
              <p className="mt-1 text-sm text-zinc-300">Rain or shine. No outside food, drinks or chairs. Small clear bags recommended. No re-entry. All sales final unless cancelled.</p>
            </div>
          </Reveal>
        </div>
        <aside className="lg:col-span-5">
          <div className="space-y-6 lg:sticky lg:top-24"><TicketOptions c={c} /><CalendarShare concert={c} /></div>
        </aside>
      </section>

      <Gallery concert={c} />

      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-12" data-testid="event-faq">
        <Eyebrow>FAQ</Eyebrow>
        <div className="mt-6"><FaqList prefix="event-faq" /></div>
      </section>
    </main>
  );
}
